import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Chip, Alert, LinearProgress,
  List, ListItem, ListItemIcon, ListItemText, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Switch, FormControlLabel, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import {
  TrendingUp, TrendingDown, AttachMoney, Analytics, SmartToy, DataUsage, 
  Insights, Schedule, Notifications, Settings, PlayArrow, Pause,
  Refresh, Add, Edit, Delete, ExpandMore, Send, ScheduleSend, Done, Error,
  Pending, CheckCircle, Warning, Info, Star, StarBorder, Timeline,
  Business, People, Assessment, Psychology, AutoAwesome, Speed
} from '@mui/icons-material';

const RevenueIntelligence = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pipelineData, setPipelineData] = useState({});
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [roiData, setRoiData] = useState({});

  // Mock pipeline data
  const mockPipelineData = {
    totalValue: 2450000,
    stages: [
      { name: 'Prospecting', value: 850000, count: 45, conversionRate: 12.5 },
      { name: 'Qualified', value: 680000, count: 28, conversionRate: 35.7 },
      { name: 'Proposal', value: 520000, count: 15, conversionRate: 66.7 },
      { name: 'Negotiation', value: 320000, count: 8, conversionRate: 87.5 },
      { name: 'Closed Won', value: 80000, count: 2, conversionRate: 100 }
    ],
    forecast: {
      currentMonth: 180000,
      nextMonth: 220000,
      quarter: 650000,
      year: 2800000
    },
    velocity: {
      avgDealSize: 54500,
      avgSalesCycle: 45,
      winRate: 23.4,
      leadToClose: 12.5
    }
  };

  // Mock performance metrics
  const mockPerformanceMetrics = {
    salesReps: [
      {
        id: 1,
        name: 'Sarah Johnson',
        quota: 500000,
        achieved: 420000,
        deals: 8,
        avgDealSize: 52500,
        conversionRate: 28.5,
        activities: 156,
        pipelineValue: 680000
      },
      {
        id: 2,
        name: 'Mike Chen',
        quota: 500000,
        achieved: 380000,
        deals: 6,
        avgDealSize: 63333,
        conversionRate: 24.2,
        activities: 142,
        pipelineValue: 520000
      },
      {
        id: 3,
        name: 'Lisa Rodriguez',
        quota: 500000,
        achieved: 290000,
        deals: 5,
        avgDealSize: 58000,
        conversionRate: 20.8,
        activities: 98,
        pipelineValue: 420000
      }
    ],
    teamMetrics: {
      totalQuota: 1500000,
      totalAchieved: 1090000,
      teamConversionRate: 24.5,
      avgDealSize: 54500,
      totalActivities: 396,
      totalPipeline: 1620000
    }
  };

  // Mock ROI data
  const mockRoiData = {
    marketingCampaigns: [
      {
        id: 1,
        name: 'LinkedIn Ads - Q4',
        spend: 15000,
        leads: 45,
        conversions: 8,
        revenue: 320000,
        roi: 2033,
        cpl: 333,
        cpa: 1875
      },
      {
        id: 2,
        name: 'Content Marketing',
        spend: 8000,
        leads: 28,
        conversions: 5,
        revenue: 180000,
        roi: 2150,
        cpl: 286,
        cpa: 1600
      },
      {
        id: 3,
        name: 'Trade Shows',
        spend: 25000,
        leads: 32,
        conversions: 6,
        revenue: 240000,
        roi: 860,
        cpl: 781,
        cpa: 4167
      }
    ],
    overallMetrics: {
      totalSpend: 48000,
      totalLeads: 105,
      totalConversions: 19,
      totalRevenue: 740000,
      overallRoi: 1442,
      avgCpl: 457,
      avgCpa: 2526
    }
  };

  useEffect(() => {
    setPipelineData(mockPipelineData);
    setPerformanceMetrics(mockPerformanceMetrics);
    setRoiData(mockRoiData);
  }, []);

  const getConversionColor = (rate) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const getRoiColor = (roi) => {
    if (roi >= 1500) return 'success';
    if (roi >= 1000) return 'warning';
    return 'error';
  };

  const renderPipeline = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Pipeline Overview</Typography>
      
      {/* Pipeline Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                ${(pipelineData.totalValue / 1000000).toFixed(1)}M
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Pipeline Value</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                ${(pipelineData.forecast.currentMonth / 1000).toFixed(0)}K
              </Typography>
              <Typography variant="body2" color="text.secondary">Current Month Forecast</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {pipelineData.velocity.avgSalesCycle} days
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg Sales Cycle</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {pipelineData.velocity.winRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">Win Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pipeline Stages */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Pipeline Stages</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Stage</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Conversion Rate</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pipelineData.stages?.map((stage, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">{stage.name}</Typography>
                        <Chip 
                          label={`${((stage.value / pipelineData.totalValue) * 100).toFixed(1)}%`} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6">${(stage.value / 1000).toFixed(0)}K</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1">{stage.count}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${stage.conversionRate}%`} 
                        color={getConversionColor(stage.conversionRate)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small">
                        <Analytics />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Forecast vs Actual */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Forecast vs Actual</Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Current Month"
                    secondary={`Forecast: $${(pipelineData.forecast?.currentMonth / 1000).toFixed(0)}K`}
                  />
                  <Chip label="On Track" color="success" size="small" />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Next Month"
                    secondary={`Forecast: $${(pipelineData.forecast?.nextMonth / 1000).toFixed(0)}K`}
                  />
                  <Chip label="Growing" color="primary" size="small" />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Quarter"
                    secondary={`Forecast: $${(pipelineData.forecast?.quarter / 1000).toFixed(0)}K`}
                  />
                  <Chip label="Strong" color="success" size="small" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Pipeline Velocity</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Avg Deal Size</Typography>
                  <Typography variant="h6" color="primary">
                    ${(pipelineData.velocity?.avgDealSize / 1000).toFixed(0)}K
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Sales Cycle</Typography>
                  <Typography variant="h6" color="warning.main">
                    {pipelineData.velocity?.avgSalesCycle} days
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                  <Typography variant="h6" color="success.main">
                    {pipelineData.velocity?.winRate}%
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Lead to Close</Typography>
                  <Typography variant="h6" color="info.main">
                    {pipelineData.velocity?.leadToClose}%
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderPerformance = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Sales Performance</Typography>
      
      {/* Team Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                ${(performanceMetrics.teamMetrics?.totalAchieved / 1000000).toFixed(1)}M
              </Typography>
              <Typography variant="body2" color="text.secondary">Team Revenue</Typography>
              <LinearProgress 
                variant="determinate" 
                value={(performanceMetrics.teamMetrics?.totalAchieved / performanceMetrics.teamMetrics?.totalQuota) * 100}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {performanceMetrics.teamMetrics?.teamConversionRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">Team Conversion Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                ${(performanceMetrics.teamMetrics?.avgDealSize / 1000).toFixed(0)}K
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg Deal Size</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {performanceMetrics.teamMetrics?.totalActivities}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Activities</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sales Rep Performance */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Sales Representative Performance</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rep</TableCell>
                  <TableCell align="right">Quota</TableCell>
                  <TableCell align="right">Achieved</TableCell>
                  <TableCell align="right">Deals</TableCell>
                  <TableCell align="right">Conversion Rate</TableCell>
                  <TableCell align="right">Pipeline</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {performanceMetrics.salesReps?.map((rep) => (
                  <TableRow key={rep.id}>
                    <TableCell>
                      <Typography variant="subtitle2">{rep.name}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      ${(rep.quota / 1000).toFixed(0)}K
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography variant="h6">${(rep.achieved / 1000).toFixed(0)}K</Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={(rep.achieved / rep.quota) * 100}
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {rep.deals}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${rep.conversionRate}%`} 
                        color={getConversionColor(rep.conversionRate)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      ${(rep.pipelineValue / 1000).toFixed(0)}K
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small">
                        <Analytics />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const renderROI = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Marketing ROI Analysis</Typography>
      
      {/* Overall ROI Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                ${(roiData.overallMetrics?.totalRevenue / 1000).toFixed(0)}K
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {roiData.overallMetrics?.overallRoi}%
              </Typography>
              <Typography variant="body2" color="text.secondary">Overall ROI</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                ${roiData.overallMetrics?.avgCpl}
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg Cost per Lead</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                ${roiData.overallMetrics?.avgCpa}
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg Cost per Acquisition</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaign Performance */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Campaign Performance</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell align="right">Spend</TableCell>
                  <TableCell align="right">Leads</TableCell>
                  <TableCell align="right">Conversions</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">ROI</TableCell>
                  <TableCell align="right">CPL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roiData.marketingCampaigns?.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <Typography variant="subtitle2">{campaign.name}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      ${(campaign.spend / 1000).toFixed(0)}K
                    </TableCell>
                    <TableCell align="right">
                      {campaign.leads}
                    </TableCell>
                    <TableCell align="right">
                      {campaign.conversions}
                    </TableCell>
                    <TableCell align="right">
                      ${(campaign.revenue / 1000).toFixed(0)}K
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${campaign.roi}%`} 
                        color={getRoiColor(campaign.roi)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      ${campaign.cpl}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 0.25, sm: 1 }, pb: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>Revenue Intelligence</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Real-time pipeline value, conversion rate optimization, and ROI tracking for data-driven decisions
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>AI-Powered Insights:</strong> Our revenue intelligence system provides real-time analytics, 
          predictive forecasting, and actionable insights to optimize your sales performance and marketing ROI.
        </Typography>
      </Alert>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Pipeline" />
        <Tab label="Performance" />
        <Tab label="ROI Analysis" />
      </Tabs>

      {activeTab === 0 && renderPipeline()}
      {activeTab === 1 && renderPerformance()}
      {activeTab === 2 && renderROI()}
    </Box>
  );
};

export default RevenueIntelligence;
